/** @module store/ems_variable_stores */

import { getEmsVariableStores, getEmsVariableStore, updateEmsVariableStore } from '@/api/ems_variable_stores'

/**
 * Allows to store an `Ems::VariableStore` (c.f. rails model) by its ID.
 *
 * A stored `vStore` has `name, storeType, schema, uiSchema, data`.
 *
 * The `vStore`'s `schema, uiSchema, data` are specified by [jsonforms]{@link https://jsonforms.io/}
 *
 */
const state = () => ({
  vStores: {}
})

const getters = {
  vStoreIds: (state) => {
    return Object.keys(state.vStores)
  },
  vStores: (state) => {
    return Object.values(state.vStores).sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
  },
  getVStoreById: (state) => {
    return (id) => {
      return state.vStores[id]
    }
  },
  getVStoreNameById: (state) => {
    return (id) => {
      return state.vStores[id]?.name
    }
  },
  getVStoreDataById: (state) => {
    return (id) => {
      return state.vStores[id]?.data
    }
  }
}

/**
 * Mutations to set and update the `vStores`.
 *
 * A `vStore` has to have `id, name, storeType, schema, uiSchema, data`.
 *
 */
const mutations = {
  SET_VSTORE: (state, vStore) => {
    state.vStores[vStore.id] = vStore
  },
  SET_SCHEMAS_VSTORE: (state, { id, schema, uiSchema }) => {
    const vStore = state.vStores[id]

    if (!vStore) {
      return
    }

    vStore.schema = schema
    vStore.uiSchema = uiSchema
  },
  SET_DATA_VSTORE: (state, { id, data }) => {
    const vStore = state.vStores[id]

    if (!vStore) {
      return
    }

    vStore.data = data
  },
  CLEAR_VSTORES: (state) => {
    state.vStores = {}
  }
}

const actions = {
  // The BE will return a list of `Ems::VariableStore`(s),
  // with `name, storeType` attributes only.
  //
  getEmsVariableStores({ commit }, { ctxt, emsId, storeType }) {
    return getEmsVariableStores({
      ctxt,
      emsId,
      storeType
    }).then((rsp) => {
      rsp.payload.data.forEach((d) => {
        commit('SET_VSTORE', {
          id: d.id,
          ...d.attributes
        })
      })
    })
  },
  getEmsVariableStore({ commit, rootGetters }, { ctxt, emsId, id, locale }) {
    if (!locale) {
      locale = rootGetters['user/locale']
    }

    return getEmsVariableStore({
      ctxt,
      emsId,
      id,
      locale
    }).then((rsp) => {
      const d = rsp.payload.data
      commit('SET_SCHEMAS_VSTORE', {
        id: d.id,
        schema: d.attributes.schema,
        uiSchema: d.attributes.uiSchema
      })
      commit('SET_DATA_VSTORE', {
        id: d.id,
        data: d.attributes.data
      })
    })
  },
  updateEmsVariableStore({ getters }, { ctxt, emsId, id }) {
    return updateEmsVariableStore({
      ctxt,
      emsId,
      id,
      data: getters.getVStoreDataById(id)
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
